import { startStreamingResponse } from '../api-fetch';
import { streamResponsePreview } from './utils/map-response-for-single-key-to-markdown';
import { getSupportedMarkdowns } from './utils/get-supported-markdown-from-schema';
import { runContextListAgainstFFs } from './utils/strip-context-list';
import { convoAIStatelessApiURL } from '../../prebuilt/utils';

/**
 * Transforms some text based on a transformation type.
 *
 * Intended for use in EditorPluginAI config items.
 * @see {@link [EditorPluginAI](../../editor-plugin-ai.tsx)}
 *
 * @example
 * ```ts
 * triggerPromptRequest({ initialContext }) {
 *   return transform({
 *     text: initialContext.doc,
 *     transformationType: 'shorten',
 *   });
 * }
 * ```
 *
 * @see https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Transform-API-Signature---Non-Streaming
 */

/**
 * Transforms some text based on a transformation type.
 *
 * Intended for use in EditorPluginAI config items.
 * @see {@link [EditorPluginAI](../../editor-plugin-ai.tsx)}
 *
 * @example
 * ```ts
 * triggerPromptRequest({ initialContext }) {
 *   return transform({
 *     text: initialContext.doc,
 *     transformationType: 'improve_quality',
 *   });
 * }
 * ```
 *
 * @see https://hello.atlassian.net/wiki/spaces/CA3/pages/2485466879/Generative+AI+API+Specs#Transform-API-Signature---Non-Streaming
 */

// Note -- the current plan is to collapse api interactions into a single
// mechanism as part of our move to "api v2"
// https://product-fabric.atlassian.net/wiki/spaces/EUXQ/pages/3565551810/Generative+AI+BE+API+Current+Interim+Future
export const transform = input => {
  return function promptRequest(requestOptions) {
    const streamingResponseGenerator = startStreamingResponse({
      endpoint: requestOptions.generativeAIApiUrl,
      payload: JSON.stringify({
        intentSchemaId: input.intentSchemaId,
        contextList: runContextListAgainstFFs(input.contextList),
        modelPlatform: 'OPENAI',
        outputLocale: input.userLocale,
        streaming: true,
        outputFormat: {
          outputFormatType: 'MARKDOWN',
          markdowns: getSupportedMarkdowns(input.editorSchema)
        },
        product: requestOptions.product,
        ...(input.backendModel === 'einstein' && {
          backendExperiment: 'EINSTEIN'
        }),
        targetTranslationLanguage: input.intentSchemaId === 'translate_intent_schema.json' ? input.targetTranslationLanguage : undefined
      }),
      ...requestOptions
    });
    return streamResponsePreview({
      streamingResponseGenerator
    });
  };
};
export const transformConvoAI = input => {
  return function promptRequest(requestOptions) {
    // If the current selection is the same as the full document we don't
    // need to send it to the backend as it will get it from the full document
    let draft_selected_content = input.currentSelection;
    if (input.fullDocument === input.currentSelection) {
      draft_selected_content = undefined;
    }
    const streamingResponseGenerator = startStreamingResponse({
      endpoint: convoAIStatelessApiURL,
      payload: JSON.stringify({
        recipient_agent_named_id: 'editor_intent_agent',
        agent_input_context: {
          transformation_type: input.transformationType,
          transformation_subtype: 'transformationSubType' in input ? input.transformationSubType : undefined,
          writing_intent: input.intentSchemaId,
          draft_full_content: input.fullDocument,
          draft_selected_content,
          supported_markdown_nodes: getSupportedMarkdowns(input.editorSchema),
          input_mime_type: 'text/markdown',
          output_mime_type: 'text/markdown',
          locale: input.userLocale,
          product: requestOptions.product
        }
      }),
      ...requestOptions
    });
    return streamResponsePreview({
      streamingResponseGenerator,
      isConvoAI: true
    });
  };
};

// TODO - change this function based on the recommendation from this slack thread: https://atlassian.slack.com/archives/C04039CJ003/p1712717030031689
// TODO https://jplat.jira.atlassian.cloud/browse/JNLIS-900

// TODO - change this function based on the recommendation from this slack thread: https://atlassian.slack.com/archives/C04039CJ003/p1712717030031689
// TODO https://jplat.jira.atlassian.cloud/browse/JNLIS-900
export const unstable_transformConvoAI_IssueReformat = input => {
  return function promptRequest(requestOptions) {
    let issue_description = input.fullDocument;
    const streamingResponseGenerator = startStreamingResponse({
      endpoint: convoAIStatelessApiURL,
      experienceId: 'issue-reformatter',
      agentNameId: input.agentNamedId,
      payload: JSON.stringify({
        recipient_agent_named_id: input.agentNamedId,
        agent_input_context: {
          locale: input.userLocale,
          issue_description,
          ...(input === null || input === void 0 ? void 0 : input.additionalPayload)
        }
      }),
      ...requestOptions
    });
    return streamResponsePreview({
      streamingResponseGenerator,
      isConvoAI: true
    });
  };
};